/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

// UI lib components
import { notifications } from '@mantine/notifications';

// Lib
import { apiClient } from '@lib/api-client';
import { queryClient } from '@lib/react-query';

// Types
import { TBodyLogin, TApiResponseLogin } from '../types';

// Store
import useStore from '@store/index';

// Types
import { TApiError } from '@global-types/api/api';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import { useLocalStorage } from '@hooks/useLocalStorage';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const login = (
	body: TBodyLogin
): Promise<TApiResponseLogin> => {
	return apiClient.post('/auth/login', {
		...body,
		withCredentials: true,
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useLogin = () => {
	const navigate = useNavigate();
	const { t, i18n } = useTranslation();
	const { setUser: setUser } = useStore();
	const [lng, setLng] = useLocalStorage('lng', 'fr');

	return useMutation({
		mutationFn: login,
		onSuccess: (data: TApiResponseLogin) => {
			queryClient.setQueryData(
				[queryKeys.auth.user.key],
				data
			);
			queryClient.invalidateQueries({
				queryKey: [queryKeys.whoAmI.key],
			});
			setUser(data);
			if (data?.locale?.language) {
				i18n.changeLanguage(data?.locale?.language);
				setLng(data?.locale?.language);
			}

			navigate('/');
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'error',
			});
		},
	});
};
